/**
 * Action-visibility rules for the Sessions table (rules locked with Ray
 * 2026-06-12). A customer MAC repeats across rows (history), so only one
 * row per (device, mac) may carry the "+Time" action:
 *
 *   1. The MAC has an active/paused row → only that live row gets +Time.
 *      Its expired siblings get nothing (extending one while another is
 *      live would create two overlapping sessions).
 *   2. No live row → only the NEWEST expired/kicked row gets +Time
 *      (re-activation). Older repeats are history only.
 *   3. Whitelisted MACs (virtual Unlimited row present) get no +Time on
 *      any row — their action is Kick on the Unlimited row itself, which
 *      removes the whitelist.
 *   4. Data sessions never get +Time (quota-based, server rejects).
 */

export interface SessionActionRow {
   id: string;
   mac: string;
   deviceId: string | null;
   status: 'active' | 'expired' | 'paused';
   isWhitelist?: boolean;
   isDataSession?: boolean;
   createdAt: string;
}

const key = (s: SessionActionRow) => `${s.deviceId ?? 'none'}|${s.mac}`;

/** Row ids that should render the "+Time" action. */
export function extendableSessionIds(rows: SessionActionRow[]): Set<string> {
   const liveByKey = new Set<string>();
   const whitelistedByKey = new Set<string>();
   for (const s of rows) {
      if (s.isWhitelist) {
         whitelistedByKey.add(key(s));
         continue;
      }
      if (s.status === 'active' || s.status === 'paused') {
         liveByKey.add(key(s));
      }
   }

   const out = new Set<string>();
   // Live rows: +Time / +Data on the live row itself (active only — a paused
   // session must be resumed first, same as today's behavior). Data rows
   // count too: the operator adds megabytes to them (Ray 2026-09-15).
   for (const s of rows) {
      if (s.isWhitelist) continue;
      if (s.status === 'active' && !whitelistedByKey.has(key(s))) {
         out.add(s.id);
      }
   }

   // Expired rows: newest per (device, mac) when nothing is live and the
   // MAC is not whitelisted on that device.
   const newestExpired = new Map<string, SessionActionRow>();
   for (const s of rows) {
      if (s.isWhitelist) continue;
      if (s.status !== 'expired') continue;
      const k = key(s);
      if (liveByKey.has(k) || whitelistedByKey.has(k)) continue;
      const prev = newestExpired.get(k);
      if (!prev || s.createdAt > prev.createdAt) newestExpired.set(k, s);
   }
   for (const s of newestExpired.values()) out.add(s.id);

   return out;
}

/* ─── Awaiting devices (Sessions → "Awaiting" tab) ──────────── */

/** Canonical MAC: uppercase, colon-separated — matches the server. */
export const normalizeMac = (mac: string): string =>
   mac.trim().toUpperCase().replace(/-/g, ':');

export interface RawLease {
   mac: string;
   address: string;
   hostname: string;
}

export interface DeviceLeases {
   deviceId: string;
   deviceName: string;
   leases: RawLease[];
}

export interface AwaitingDevice {
   deviceId: string;
   deviceName: string;
   // Best-effort friendly name for the row: the DHCP hostname when it
   // sent one, else a vendor guess from the MAC, else "Private device"
   // (random MAC) / "Unknown device".
   name: string;
   mac: string;
   ip: string;
   hostname: string;
}

/** OUI (first 3 bytes) → Espressif. NodeMCUes are ESP chips, so
 * these are always infrastructure to hide. Common ESP8266/ESP32 prefixes. */
const ESPRESSIF_OUIS = new Set([
   '18:FE:34', '24:0A:C4', '24:6F:28', '24:B2:DE', '2C:3A:E8', '30:AE:A4',
   '3C:61:05', '3C:71:BF', '48:3F:DA', '4C:11:AE', '4C:75:25', '54:43:B2',
   '5C:CF:7F', '60:01:94', '68:C6:3A', '70:03:9F', '7C:9E:BD', '7C:DF:A1',
   '80:7D:3A', '84:0D:8E', '84:CC:A8', '84:F3:EB', '8C:AA:B5', '90:38:0C',
   '94:B9:7E', '98:CD:AC', '9C:9C:1F', 'A0:20:A6', 'A4:7B:9D', 'A4:CF:12',
   'A4:E5:7C', 'AC:0B:FB', 'B4:E6:2D', 'B8:F0:09', 'BC:DD:C2', 'C4:4F:33',
   'C4:5B:BE', 'CC:50:E3', 'D8:A0:1D', 'D8:BF:C0', 'DC:4F:22', 'E0:98:06',
   'E8:31:CD', 'E8:68:E7', 'E8:DB:84', 'EC:64:C9', 'EC:FA:BC', 'F4:CF:A2',
]);

/** OUI → consumer-brand label (display only — these are KEPT, not hidden). */
const VENDOR_OUIS: Record<string, string> = {
   // Apple
   '00:CD:FE': 'Apple', '3C:15:C2': 'Apple', '7C:D1:C3': 'Apple',
   'A4:83:E7': 'Apple', 'F0:18:98': 'Apple', 'AC:BC:32': 'Apple',
   // Samsung
   '00:12:FB': 'Samsung', '34:23:BA': 'Samsung', '5C:0A:5B': 'Samsung',
   '8C:77:12': 'Samsung', 'C8:BA:94': 'Samsung',
   // Xiaomi
   '64:09:80': 'Xiaomi', '78:11:DC': 'Xiaomi', 'F8:A4:5F': 'Xiaomi',
   // Huawei / Honor
   '00:E0:FC': 'Huawei', '48:46:FB': 'Huawei', '70:72:3C': 'Huawei',
   // Oppo / Realme / Vivo / Transsion (Tecno/Infinix/itel)
   '94:E9:79': 'Oppo', 'C8:78:43': 'Vivo', 'D4:64:F7': 'Tecno',
   // Google, Intel (laptops)
   '3C:5A:B4': 'Google', '34:13:E8': 'Intel', '8C:F5:A3': 'Intel',
};

/** AP / router / switch hostnames — networking gear, never a customer. */
const INFRA_HOSTNAME_RE =
   /(^ESP[-_])|EAP\d|UAP|UniFi|Omada|Deco|Archer|TL-(WA|WR|SG|WN)|Outdoor|Mikrotik|RB\d|hAP|Ruijie|Reyee|Ruckus|Aruba|Cisco|Netgear|Tenda|Linksys|AP[-_]?\d|router|switch|gateway/i;

const ouiOf = (mac: string): string => normalizeMac(mac).slice(0, 8);

/** Locally-administered bit set = a randomized / private MAC (modern
 * phones default to this), so the real vendor can't be derived. */
export function isRandomMac(mac: string): boolean {
   const first = parseInt(normalizeMac(mac).slice(0, 2), 16);
   return Number.isFinite(first) && (first & 0x02) === 0x02;
}

/** True when this lease is infrastructure (NodeMCU, AP, router) and
 * should be hidden from the operator's Awaiting list. */
export function looksLikeInfra(
   mac: string,
   hostname: string,
   infraKeysHit: boolean
): boolean {
   if (infraKeysHit) return true; // known nodeMcu / operator-bypass MAC
   if (ESPRESSIF_OUIS.has(ouiOf(mac))) return true; // NodeMCU
   if (hostname && INFRA_HOSTNAME_RE.test(hostname)) return true; // AP/router
   return false;
}

/** Display name for an awaiting row. */
export function friendlyDeviceName(mac: string, hostname: string): string {
   if (hostname && hostname.trim()) return hostname.trim();
   const vendor = VENDOR_OUIS[ouiOf(mac)];
   if (vendor) return `${vendor} device`;
   if (isRandomMac(mac)) return 'Private device';
   return 'Unknown device';
}

/**
 * "Awaiting" = a device that joined the WiFi (has a DHCP lease) but has
 * NO live (active/paused) session and is NOT whitelisted. These are the
 * devices an operator grants time to / whitelists by hand — e.g. a smart
 * TV that can't run the captive flow.
 *
 * `liveKeys`, `whitelistKeys`, `infraKeys` are sets of `${deviceId}|${MAC}`
 * (MAC normalized). `infraKeys` holds known NodeMCU / operator-bypass
 * MACs. Infrastructure (NodeMCU, APs, routers) is also dropped by OUI /
 * hostname so the list shows only real customer devices. De-dupes a MAC
 * that appears twice in the lease table.
 */
export function computeAwaitingDevices(
   perDevice: DeviceLeases[],
   liveKeys: Set<string>,
   whitelistKeys: Set<string>,
   infraKeys: Set<string> = new Set()
): AwaitingDevice[] {
   const out: AwaitingDevice[] = [];
   const seen = new Set<string>();
   for (const d of perDevice) {
      for (const lease of d.leases) {
         const mac = normalizeMac(lease.mac || '');
         if (!mac) continue;
         const k = `${d.deviceId}|${mac}`;
         if (seen.has(k)) continue;
         if (liveKeys.has(k) || whitelistKeys.has(k)) continue;
         const hostname = lease.hostname || '';
         if (looksLikeInfra(mac, hostname, infraKeys.has(k))) continue;
         seen.add(k);
         out.push({
            deviceId: d.deviceId,
            deviceName: d.deviceName,
            name: friendlyDeviceName(mac, hostname),
            mac,
            ip: lease.address || '',
            hostname,
         });
      }
   }
   return out;
}

/**
 * Which vendo a lease belongs to. A router hands out every VLAN's addresses and
 * reports them all under the MAIN vendo, so a phone on sub-vendo VLAN 33 must be
 * named from its address (10.33.0.x), not from the router it was read from.
 * No match (main LAN, or a zone with no sub-vendo yet) = the main's name.
 */
export function vendoNameForLease(
   ip: string,
   main: { name: string; vlans?: { vlanId: number; subnet: string }[] },
   subs: { name: string; vlanRef?: number | null }[]
): string {
   const prefix = ip.split('.').slice(0, 3).join('.');
   const zone = main.vlans?.find((z) => z.subnet.split('/')[0].split('.').slice(0, 3).join('.') === prefix);
   if (!ip || !zone) return main.name;
   return subs.find((d) => d.vlanRef === zone.vlanId)?.name ?? main.name;
}

/** MAC -> the name the phone gave its router, from every router's leases. */
export function phoneNamesFromLeases(perDevice: DeviceLeases[]): Record<string, string> {
   const out: Record<string, string> = {};
   for (const d of perDevice) {
      for (const lease of d.leases) {
         const mac = normalizeMac(lease.mac || '');
         if (mac && lease.hostname) out[mac] = lease.hostname;
      }
   }
   return out;
}

/** Synthetic whitelist row ids look like wl:<deviceId>:<MAC>. */
export function isWhitelistRowId(id: string): boolean {
   return id.startsWith('wl:');
}

/** Extract {deviceId, mac} from a synthetic whitelist row id. */
export function parseWhitelistRowId(
   id: string
): { deviceId: string; mac: string } | null {
   const m = id.match(/^wl:([^:]+):(.+)$/);
   if (!m) return null;
   return { deviceId: m[1], mac: m[2] };
}
